import AppVersion from '../components/AppVersion';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Wrench,
  Briefcase,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Navigation,
  MessageSquare,
  MessageCircle,
  Plus,
  Edit,
  Save,
  X,
  Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import ProcessPaymentDialog from '../components/ProcessPaymentDialog';
import { useLocationTracking } from '../hooks/useLocationTracking';
import { stripHtml } from '../lib/utils';
import { allowLeave } from '../lib/leaveGuard';

const TechJobDetails = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [communications, setCommunications] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [inRoutePending, setInRoutePending] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [etaDialog, setEtaDialog] = useState({ open: false, action: '', minutes: '15', durationText: null });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [creatingWO, setCreatingWO] = useState(false);
  const [woId, setWoId] = useState(null);
  const [invoiceName, setInvoiceName] = useState(null);
  const [completingJob, setCompletingJob] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [sendingEstimate, setSendingEstimate] = useState(false);
  const [payDialog, setPayDialog] = useState({ open: false, amount: '', mode: 'Cash', reference_no: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentRecorded, setPaymentRecorded] = useState(null);
  // Process Payment — live card charge. Separate from payDialog above, which
  // only records a payment the tech already took (cash/check/Zelle/terminal).
  const [chargeOpen, setChargeOpen] = useState(false);
  // Commercial Customers & Multiple Service Locations, Tech App integration
  // (2026-08-12): editing the Additional Service Locations already attached
  // to THIS Estimate (as opposed to TechCreateJob.jsx's staged-at-creation
  // flow, which runs before the Estimate exists). `estimateLocations` is the
  // server's current truth for this Estimate (from GET
  // .../service-locations) so reopening a job always reflects what ERPNext
  // actually has, never stale local state. `customerLocations` is the
  // customer's full active set, used only to populate the "+ Add" picker.
  const [estimateLocations, setEstimateLocations] = useState([]);
  const [customerLocations, setCustomerLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [newLocationForm, setNewLocationForm] = useState({
    location_name: '', address_line1: '', city: '', state: '', zip: '',
  });
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationActionId, setLocationActionId] = useState(null);
  const techName = localStorage.getItem('technicianName') || '';

  useLocationTracking(techName, jobId, true);

  useEffect(() => {
    loadJob();
  }, [jobId]);

  // Silent background poll while an estimate is out for customer approval —
  // this is the ONLY way a tech learns the customer approved remotely (e.g.
  // signed via the portal email link 20 minutes later). loadJob() above only
  // ever runs once, on mount/jobId change, so without this the tech would have
  // no signal short of leaving and re-entering the screen. Stops itself the
  // moment the status resolves (Approved/Declined) so it doesn't poll forever.
  useEffect(() => {
    if (!job || job.custom_approval_status !== 'Sent for Approval') return undefined;
    const interval = setInterval(async () => {
      try {
        const result = await crmBackend.getQuotationDetail(jobId);
        if (!result.success || !result.data) return;
        const q = result.data;
        if (q.custom_approval_status === job.custom_approval_status) return; // still pending
        setJob(j => ({
          ...j,
          custom_approval_status: q.custom_approval_status || j.custom_approval_status,
          custom_pipeline_status: q.custom_pipeline_status || j.custom_pipeline_status,
          custom_payment_status: q.custom_payment_status || j.custom_payment_status,
        }));
        if (q.custom_approval_status === 'Approved') {
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          toast({ title: 'Estimate Approved!', description: 'The customer approved — safe to continue the job.' });
        } else if (q.custom_approval_status === 'Declined') {
          toast({ title: 'Estimate Declined', description: 'The customer declined this estimate.', variant: 'destructive' });
        }
      } catch {
        // Network hiccup — try again on the next tick, don't surface a toast for this.
      }
    }, 20000);
    return () => clearInterval(interval);
  }, [job, jobId, toast]);

  const loadJob = async () => {
    setLoading(true);
    const result = await crmBackend.getQuotationDetail(jobId);
    if (result.success && result.data) {
      const q = result.data;
      const mapped = {
        id: q.name,
        name: q.name,
        customer_name: q.party_name,
        // party_name IS the Customer doctype's `name` (ID) for a
        // quotation_to="Customer" Estimate -- same value TechCreateJob.jsx
        // gets from customer.id -- kept as its own field since customer_name
        // above is used for display text, and the two must never be
        // conflated even though they're the same string today.
        customer_id: q.party_name,
        // Service Locations only ever belong to a Customer, never a Lead --
        // Lead-sourced Estimates (quotation_to === "Lead") don't get the
        // Additional Service Locations section at all.
        quotation_to: q.quotation_to,
        custom_service_location: q.custom_service_location || '',
        // contact_mobile comes from the linked Contact (often blank on a
        // quotation_to="Customer" Estimate with no Contact row); custom_customer_phone
        // is the field this app's own Quotation hooks always keep populated from the
        // Customer/Lead — added as the real fallback 2026-09-24 after a live SMS send
        // failed with a confusing "SMS send failed (200)" toast: job.customer_phone was
        // blank (Paula Brehm/E260450 has no Contact but does have custom_customer_phone
        // "214 3840577"), so Flowroute correctly 422'd on an empty "to" number, and the
        // failure surfaced as the HTTP status code instead of a real reason (see the
        // /api/sms/send fix in tech-backend/server.py in the same commit). `contact_phone`
        // was removed — it is not a real field on Quotation (confirmed via ERPNext's own
        // "Field not permitted in query" error), so it always evaluated to undefined.
        customer_phone: q.contact_mobile || q.custom_customer_phone || '',
        address: q.custom_service_address || '',
        scheduled_date: q.custom_scheduled_date || '',
        scheduled_time: q.custom_scheduled_time || '',
        status: crmBackend.normalizeJobStatus(q.custom_job_status),
        custom_approval_status: q.custom_approval_status || '',
        custom_pipeline_status: q.custom_pipeline_status || '',
        custom_payment_status: q.custom_payment_status || '',
        description: stripHtml(q.custom_service_description),
        items: (q.items || []).map((item, idx) => ({
          idx,
          item_code: item.item_code,
          item_name: item.item_name,
          zone: item.custom_zone || '',
          qty: item.qty,
          rate: item.rate,
          amount: item.amount,
        })),
        grand_total: q.grand_total,
      };
      setJob(mapped);
      setNotes(mapped.description || '');

      // A Work Order (and possibly an invoice) may already exist from a prior visit —
      // hydrate it so "Complete Job" doesn't create a duplicate Work Order.
      try {
        const woResult = await crmBackend.getWorkOrderDetail(q.name);
        if (woResult?.data?.wo_id) {
          setWoId(woResult.data.wo_id);
          if (woResult.data.invoice_name) setInvoiceName(woResult.data.invoice_name);
        }
      } catch {
        // No Work Order yet — expected for a job that hasn't been completed.
      }

      if (mapped.quotation_to === 'Customer' && mapped.customer_id) {
        setLocationsLoading(true);
        try {
          const [estResult, custResult] = await Promise.all([
            crmBackend.getEstimateServiceLocations(q.name),
            crmBackend.getCustomerServiceLocations(mapped.customer_id),
          ]);
          setEstimateLocations(estResult.additional || []);
          setCustomerLocations(custResult.locations || []);
        } catch {
          // Server is the source of truth here — on failure, leave both
          // lists empty rather than guessing, so the tech sees "no saved
          // locations" instead of a stale/wrong set.
          setEstimateLocations([]);
          setCustomerLocations([]);
        } finally {
          setLocationsLoading(false);
        }

        // Communications history — same endpoint/shape ContactDetail.jsx's
        // history workspace uses (2026-08-14), surfaced directly on the job
        // screen too so a tech doesn't have to navigate away to the
        // separate Contact page to see what's already been sent about this
        // customer (e.g. an En Route SMS sent for this exact job).
        setCommunicationsLoading(true);
        try {
          const historyResult = await crmBackend.getContactHistory(mapped.customer_id, 'customer');
          setCommunications(historyResult?.data?.communications || []);
        } catch {
          setCommunications([]);
        } finally {
          setCommunicationsLoading(false);
        }
      }
    }
    setLoading(false);
  };

  // Locations already attached to this Estimate, or matching its primary
  // address, are excluded from the "+ Add" picker so the same property
  // can't be offered twice from this screen -- the server (ownership +
  // already-present checks inside add_additional_service_location) is
  // still the real enforcement boundary, this is just so the tech isn't
  // shown a choice that would be rejected.
  const availableCustomerLocations = customerLocations.filter(
    loc => loc.name !== job?.custom_service_location
      && !estimateLocations.some(a => a.service_location === loc.name)
  );

  // Editable mirrors the server's _quotation_is_editable definition closely
  // enough for UI purposes (hide the controls once locked) -- the server
  // call itself is still the real check; a stale client guess here just
  // means a rejected request with a toast, never a bypass.
  const locationsEditable = job
    && job.status !== 'Completed' && job.status !== 'Cancelled'
    && !['Sent for Approval', 'Approved', 'Declined'].includes(job.custom_approval_status);

  const handleAddEstimateLocation = async (loc) => {
    setLocationActionId(loc.name);
    try {
      const result = await crmBackend.addEstimateServiceLocation(job.name, loc.name);
      setEstimateLocations(result.additional || []);
      setLocationPickerOpen(false);
      toast({ title: 'Location added', description: `${loc.location_name} added to this job.` });
    } catch (err) {
      toast({
        title: 'Could not add location',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLocationActionId(null);
    }
  };

  const handleRemoveEstimateLocation = async (serviceLocationId) => {
    setLocationActionId(serviceLocationId);
    try {
      const result = await crmBackend.removeEstimateServiceLocation(job.name, serviceLocationId);
      setEstimateLocations(result.additional || []);
    } catch (err) {
      toast({
        title: 'Could not remove location',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLocationActionId(null);
    }
  };

  const handleCreateAndAddEstimateLocation = async () => {
    if (!newLocationForm.location_name.trim() || !newLocationForm.address_line1.trim()) {
      toast({ title: 'Missing info', description: 'Location name and address are required.', variant: 'destructive' });
      return;
    }
    setSavingLocation(true);
    try {
      const created = await crmBackend.createCustomerServiceLocation(job.customer_id, newLocationForm);
      const loc = { name: created.service_location_id, location_name: newLocationForm.location_name };
      setCustomerLocations(prev => [...prev, {
        name: created.service_location_id,
        location_name: newLocationForm.location_name,
        address_line1: newLocationForm.address_line1,
        city: newLocationForm.city,
        state: newLocationForm.state,
        zip: newLocationForm.zip,
      }]);
      await handleAddEstimateLocation(loc);
      setNewLocationForm({ location_name: '', address_line1: '', city: '', state: '', zip: '' });
      setNewLocationOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: 'Could not save location',
        description: typeof detail === 'string' ? detail.slice(0, 200) : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleStartJob = async () => {
    setIsStarting(true);
    try {
      await crmBackend.updateJobStatus(job.name, 'In Progress');
      setJob(j => ({ ...j, status: 'In Progress' }));
      localStorage.setItem(`job_${jobId}_startTime`, new Date().toISOString());
      toast({
        title: 'Job Started',
        description: 'Marked In Progress',
      });
    } catch (err) {
      toast({
        title: 'Could not start job',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  };

  const _getGpsEta = async (action) => {
    setGettingLocation(true);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
      );
      try {
        const eta = await crmBackend.getEtaFromCoords(
          pos.coords.latitude, pos.coords.longitude, job.address
        );
        // /api/maps/eta returns {eta_text, eta_minutes, distance_text} — NOT
        // duration_seconds/duration_text (those field names never existed on
        // this endpoint's response, so the "real" GPS ETA never rendered;
        // this silently fell through to the catch below and showed the
        // manual 15-min fallback instead, every time).
        setEtaDialog({ open: true, action, minutes: String(eta.eta_minutes), durationText: eta.eta_text });
      } catch {
        setEtaDialog({ open: true, action, minutes: '15', durationText: null });
      }
    } catch {
      // GPS denied or unavailable — fall back to manual
      setEtaDialog({ open: true, action, minutes: '15', durationText: null });
    } finally {
      setGettingLocation(false);
    }
  };

  const handleSendOnWay = () => _getGpsEta('on-way');
  const handleMarkInRoute = () => _getGpsEta('in-route');

  const _techName = () => {
    try { return JSON.parse(localStorage.getItem('techUser')).name; } catch { return 'your technician'; }
  };

  const _etaStr = (minutes) => {
    return new Date(Date.now() + parseInt(minutes || 15) * 60000)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const confirmEta = async () => {
    const { action, minutes } = etaDialog;
    setEtaDialog(d => ({ ...d, open: false }));
    const etaStr = _etaStr(minutes);
    const firstName = job.customer_name.split(' ')[0];
    const techName = _techName();

    if (action === 'in-route') {
      setInRoutePending(true);
      const message = `Hi ${firstName}, this is ${techName} from AAA Irrigation Service. I'm heading to your location now and should arrive around ${etaStr}. Thank you!`;
      try {
        await crmBackend.updateJobStatus(job.name, 'En Route');
        setJob(j => ({ ...j, status: 'En Route' }));
      } catch (err) {
        toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
        setInRoutePending(false);
        return; // ERP write failed — don't send an SMS implying a status that was never saved
      }
      // fetch() only rejects on a network failure — it does NOT throw on a
      // non-2xx response, so a backend-side failure (bad/blank phone,
      // Flowroute rejecting the send) was previously swallowed silently and
      // showed a false "Customer notified" success toast. Now checks
      // response.ok AND the backend's own {status: 'success'|'error'} body.
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
          body: JSON.stringify({ to: job.customer_phone, message, job_id: jobId, message_type: 'en_route' }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.status !== 'success') {
          throw new Error(body.detail || body.message || `SMS send failed (${res.status})`);
        }
        toast({ title: 'Marked In Route', description: `Customer notified — ETA ${etaStr}` });
      } catch (err) {
        toast({ title: 'Status updated, SMS failed', description: err.message, variant: 'destructive' });
      } finally {
        setInRoutePending(false);
      }
    } else {
      const message = `Hi ${firstName}, this is ${techName} from AAA Irrigation Service. I'm on my way to your location and should arrive around ${etaStr}. See you soon!`;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
          body: JSON.stringify({ to: job.customer_phone, message, job_id: jobId, message_type: 'en_route' }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.status !== 'success') {
          throw new Error(body.detail || body.message || `SMS send failed (${res.status})`);
        }
        toast({ title: 'SMS Sent', description: `"On my way" sent — ETA ${etaStr}` });
      } catch (err) {
        toast({ title: 'SMS failed', description: err.message, variant: 'destructive' });
      }
    }
  };

  const handle3CXCall = (phoneNumber) => {
    // 3CX Click-to-Call integration
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    
    // Try 3CX desktop app first
    const c3cxUrl = `c3cx://call/${cleanPhone}`;
    allowLeave();
    window.location.href = c3cxUrl;
    
    // Fallback to regular tel: link
    setTimeout(() => {
      window.location.href = `tel:${phoneNumber}`;
    }, 500);

    // Log call in ERPNext
    crmBackend.log3CXCall(jobId, {
      customer_name: job.customer_name,
      phone_number: phoneNumber,
      call_time: new Date().toISOString()
    });

    toast({
      title: 'Calling Customer',
      description: `Dialing ${job.customer_name}...`,
    });
  };

  const openWaze = () => {
    const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(job.address)}&navigate=yes`;
    window.open(wazeUrl, '_blank');
    
    toast({
      title: 'Opening Waze',
      description: 'Route loaded in Waze app',
    });
  };

  const openGoogleMaps = () => {
    const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(job.address)}`;
    window.open(mapsUrl, '_blank');
    
    toast({
      title: 'Opening Google Maps',
      description: 'Route loaded in Google Maps',
    });
  };

  const handleCompleteJob = async () => {
    setCompletingJob(true);
    try {
      await crmBackend.updateJobStatus(job.name, 'Completed');
      setJob(j => ({ ...j, status: 'Completed' }));

      const techName = localStorage.getItem('technicianName') || '';
      let currentWoId = woId;
      if (!currentWoId) {
        const woResult = await crmBackend.createWorkOrder(job.name, techName, techName);
        currentWoId = woResult.wo_id;
        setWoId(currentWoId);
      }

      const invResult = await crmBackend.generateWOInvoice(currentWoId, techName);
      setInvoiceName(invResult.invoice_name || null);

      toast({
        title: 'Job Completed',
        description: invResult.invoice_name
          ? `Invoice ${invResult.invoice_name} created`
          : 'Job marked complete',
      });
    } catch (err) {
      toast({
        title: 'Could not complete job',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setCompletingJob(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!invoiceName) return;
    setSendingInvoice(true);
    try {
      await crmBackend.sendInvoiceToCustomer(invoiceName);
      setInvoiceSent(true);
      toast({ title: 'Invoice sent', description: `${invoiceName} emailed to customer for payment.` });
    } catch (err) {
      toast({
        title: 'Could not send invoice',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  // Manual-only: emails the customer this estimate with Approve/Decline links.
  // Never fires as a side effect of another action — the tech has to press it.
  const handleSendEstimate = async () => {
    setSendingEstimate(true);
    try {
      await crmBackend.sendEstimateToCustomer(job.name);
      // Also flip the pipeline badge optimistically — otherwise the top-of-screen
      // status badge (phase 4 work) stays stuck on whatever it was at page load
      // until the next full reload, even though the button below correctly
      // switches to "Re-send…". The background poll above will reconcile this
      // with the server's real custom_pipeline_status once it changes further.
      setJob(j => ({ ...j, custom_approval_status: 'Sent for Approval', custom_pipeline_status: 'Sent for Approval' }));
      toast({ title: 'Estimate sent', description: `${job.name} emailed to the customer for approval.` });
    } catch (err) {
      toast({
        title: 'Could not send estimate',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSendingEstimate(false);
    }
  };

  // Manual-only: records a payment the tech collected on site (cash/check/Zelle/card).
  const handleSavePayment = async () => {
    const amount = parseFloat(payDialog.amount);
    if (!amount || amount <= 0) {
      toast({ title: 'Enter a payment amount', variant: 'destructive' });
      return;
    }
    setSavingPayment(true);
    try {
      const result = await crmBackend.payInvoice(invoiceName, {
        amount,
        mode_of_payment: payDialog.mode,
        reference_no: payDialog.reference_no,
      });
      setPaymentRecorded({ amount, mode: payDialog.mode, entry: result.payment_entry });
      setPayDialog(d => ({ ...d, open: false }));
      toast({ title: 'Payment recorded', description: `$${amount.toFixed(2)} ${payDialog.mode} applied to ${invoiceName}.` });
    } catch (err) {
      toast({
        title: 'Could not record payment',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await crmBackend.updateEstimate(job.name, { custom_service_description: notes });
      setJob({ ...job, description: notes });
      setEditingNotes(false);
      toast({
        title: 'Notes Saved',
        description: 'Job notes updated successfully',
      });
    } catch (err) {
      toast({
        title: 'Could not save notes',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading job…</p>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Job not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/tech/dashboard')}
              className="flex items-center space-x-2 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                <Wrench className="w-5 h-5 text-green-700" />
              </div>
              <span className="font-bold">Work Order</span>
            </div>
            <button
              onClick={() => { setChatText(''); setChatOpen(true); }}
              className="flex items-center hover:bg-green-600 p-2 rounded-lg transition-colors"
              title="Message office"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Approval status banner — unmissable, at the very top of the screen a tech
            sees right after tapping "Send Estimate for Approval". Additive to the
            existing top-badge (2026-07-25 phase 4 work) — that badge stays as-is;
            this is the specific in-flow moment John asked to make clear at a glance.
            Hidden once the job itself is Completed/Cancelled — the green "Job
            Completed" card below already covers that. */}
        {job.status !== 'Completed' && job.status !== 'Cancelled' && job.custom_approval_status === 'Approved' && (
          <div className="bg-green-600 text-white rounded-lg p-4 flex items-center gap-3 shadow-md">
            <CheckCircle2 className="w-8 h-8 flex-shrink-0" />
            <div>
              <p className="font-bold text-lg leading-tight">Approved by Customer</p>
              <p className="text-sm text-green-100">Safe to continue — go ahead and finish the job.</p>
            </div>
          </div>
        )}
        {job.status !== 'Completed' && job.status !== 'Cancelled' && job.custom_approval_status === 'Sent for Approval' && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 flex-shrink-0 text-amber-600" />
            <div>
              <p className="font-bold text-lg text-amber-900 leading-tight">Waiting for Customer Approval</p>
              <p className="text-sm text-amber-700">We'll let you know here as soon as they approve.</p>
            </div>
          </div>
        )}
        {job.status !== 'Completed' && job.status !== 'Cancelled' && job.custom_approval_status === 'Declined' && (
          <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 flex-shrink-0 text-red-600" />
            <div>
              <p className="font-bold text-lg text-red-900 leading-tight">Estimate Declined</p>
              <p className="text-sm text-red-700">The customer declined this estimate — check with the office.</p>
            </div>
          </div>
        )}

        {/* Job Header */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{job.customer_name}</h1>
                <p className="text-gray-600">{job.service_type}</p>
                <p className="text-sm text-gray-500 mt-1">WO #{job.id}</p>
              </div>
              <Badge className={(job.custom_pipeline_status
                ? crmBackend.pipelineStatusColor(job.custom_pipeline_status)
                : crmBackend.getJobStatusColor(crmBackend.displayStatus(job.status, job.custom_approval_status))) + ' border text-base px-3 py-1'}>
                {job.custom_pipeline_status || crmBackend.displayStatus(job.status, job.custom_approval_status)}
              </Badge>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={() => handle3CXCall(job.customer_phone)}
                className="flex items-center justify-center space-x-2 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>Call (3CX)</span>
              </button>
              <button
                onClick={handleSendOnWay}
                className="flex items-center justify-center space-x-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                <span>On My Way</span>
              </button>
            </div>
            
            {/* Navigation Options */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={openWaze}
                className="flex items-center justify-center space-x-2 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                <Navigation className="w-5 h-5" />
                <span>Waze</span>
              </button>
              <button
                onClick={openGoogleMaps}
                className="flex items-center justify-center space-x-2 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                <MapPin className="w-5 h-5" />
                <span>Google Maps</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium text-gray-900">{job.address}</p>
                <div className="flex space-x-3 mt-2">
                  <button
                    onClick={openWaze}
                    className="text-sm text-purple-600 hover:underline flex items-center space-x-1"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>Waze</span>
                  </button>
                  <button
                    onClick={openGoogleMaps}
                    className="text-sm text-blue-600 hover:underline flex items-center space-x-1"
                  >
                    <MapPin className="w-4 h-4" />
                    <span>Google Maps</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <button 
                  onClick={() => handle3CXCall(job.customer_phone)}
                  className="font-medium text-blue-600 hover:underline"
                >
                  {job.customer_phone}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <a href={`mailto:${job.customer_email}`} className="font-medium text-blue-600 hover:underline">
                  {job.customer_email}
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Service Locations — commercial jobs spanning more than
            one property. Reflects server truth (loaded from
            GET .../service-locations on mount) so a reload always shows the
            same set. The primary address above is untouched. Lead-sourced
            Estimates never get this section — Service Locations only ever
            belong to a Customer. */}
        {job.quotation_to === 'Customer' && (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-green-600" />
                <span>Additional Service Locations</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {locationsLoading && (
                <p className="text-sm text-gray-500">Loading locations…</p>
              )}
              {!locationsLoading && estimateLocations.length === 0 && (
                <p className="text-sm text-gray-500">No additional locations on this job.</p>
              )}
              {estimateLocations.map((loc) => (
                <div key={loc.service_location} className="flex items-start justify-between bg-gray-50 rounded-md px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{loc.location_name}</p>
                    <p className="text-xs text-gray-500">
                      {[loc.address_line1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  {locationsEditable && (
                    <button
                      type="button"
                      onClick={() => handleRemoveEstimateLocation(loc.service_location)}
                      disabled={locationActionId === loc.service_location}
                      className="text-red-600 hover:text-red-700 text-sm font-medium px-2 shrink-0 disabled:opacity-50"
                    >
                      {locationActionId === loc.service_location ? 'Removing…' : 'Remove'}
                    </button>
                  )}
                </div>
              ))}

              {locationsEditable && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setLocationPickerOpen(o => !o); setNewLocationOpen(false); }}
                    className="w-full h-11 rounded-md border border-dashed border-green-400 text-green-700 text-sm font-medium hover:bg-green-50 flex items-center justify-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Service Location</span>
                  </button>
                  {locationPickerOpen && (
                    <div className="absolute z-40 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {availableCustomerLocations.length === 0 && (
                        <div className="px-3 py-3 text-sm text-gray-500">
                          No saved Service Locations for this customer yet.
                        </div>
                      )}
                      {availableCustomerLocations.map((loc) => (
                        <button
                          key={loc.name}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          disabled={locationActionId === loc.name}
                          onClick={() => handleAddEstimateLocation(loc)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 disabled:opacity-50"
                        >
                          <p className="text-sm font-medium text-gray-900">{loc.location_name}</p>
                          <p className="text-xs text-gray-500">
                            {[loc.address_line1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                          </p>
                        </button>
                      ))}
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setLocationPickerOpen(false); setNewLocationOpen(true); }}
                        className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 font-medium"
                      >
                        + Create a new Service Location
                      </button>
                    </div>
                  )}
                </div>
              )}

              {locationsEditable && newLocationOpen && (
                <div className="border rounded-md p-3 space-y-2 bg-gray-50">
                  <Input
                    value={newLocationForm.location_name}
                    onChange={(e) => setNewLocationForm(f => ({ ...f, location_name: e.target.value }))}
                    placeholder="Location name (e.g. Warehouse B)"
                    className="h-10 text-sm"
                  />
                  <Input
                    value={newLocationForm.address_line1}
                    onChange={(e) => setNewLocationForm(f => ({ ...f, address_line1: e.target.value }))}
                    placeholder="Address"
                    className="h-10 text-sm"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={newLocationForm.city}
                      onChange={(e) => setNewLocationForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="City"
                      className="h-10 text-sm"
                    />
                    <Input
                      value={newLocationForm.state}
                      onChange={(e) => setNewLocationForm(f => ({ ...f, state: e.target.value }))}
                      placeholder="State"
                      className="h-10 text-sm"
                    />
                    <Input
                      value={newLocationForm.zip}
                      onChange={(e) => setNewLocationForm(f => ({ ...f, zip: e.target.value }))}
                      placeholder="ZIP"
                      className="h-10 text-sm"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      onClick={handleCreateAndAddEstimateLocation}
                      disabled={savingLocation}
                      className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white text-sm"
                    >
                      {savingLocation ? 'Saving…' : 'Save Location'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setNewLocationOpen(false)}
                      className="h-10 text-sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Schedule Info */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-medium text-gray-900">
                  {new Date(job.scheduled_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Time</p>
                <p className="font-medium text-gray-900">{job.scheduled_time} ({job.estimated_duration} min estimated)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Description */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Job Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{job.description}</p>
            {job.priority === 'High' && (
              <div className="mt-3 flex items-center space-x-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">High Priority</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Communications — emails/SMS already sent about this customer
            (e.g. estimate/invoice emails, En Route SMS), pulled from the
            same contact-history endpoint ContactDetail.jsx uses. Not
            filtered to this specific job — a customer's communications are
            shown in full so a tech sees the whole picture, matching how
            ContactDetail.jsx's own history isn't job-scoped either. */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Communications</CardTitle>
          </CardHeader>
          <CardContent>
            {communicationsLoading && (
              <p className="text-sm text-gray-400">Loading…</p>
            )}
            {!communicationsLoading && communications.length === 0 && (
              <p className="text-sm text-gray-400">No communications on record for this customer.</p>
            )}
            {!communicationsLoading && communications.length > 0 && (
              <div className="space-y-3">
                {communications.map((c) => {
                  const medium = (c.communication_medium || c.communication_type || '').toLowerCase();
                  const icon = medium.includes('sms') || medium.includes('text')
                    ? '💬'
                    : medium.includes('phone') || medium.includes('call')
                    ? '📞'
                    : '✉️';
                  return (
                    <div key={c.name} className="border-b last:border-0 pb-3 last:pb-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <span>{icon}</span> {c.subject || 'Communication'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {c.communication_date
                            ? new Date(c.communication_date).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                              })
                            : ''}
                        </span>
                      </div>
                      {c.content && (
                        <p className="text-sm text-gray-600 mt-1">{c.content}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Materials/Items */}
        <Card className="bg-white">
          <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <CardTitle className="text-lg">Materials & Items</CardTitle>
            <div className="flex flex-wrap gap-2">
              {woId ? (
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => navigate(`/tech/workorder/${woId}`)}>
                  <Briefcase className="w-4 h-4 mr-1" /> Work Order
                </Button>
              ) : (
                <Button size="sm" variant="outline"
                  className="text-purple-700 border-purple-300 hover:bg-purple-50"
                  disabled={creatingWO}
                  onClick={async () => {
                    setCreatingWO(true);
                    try {
                      const techName = localStorage.getItem('technicianName') || '';
                      const result = await crmBackend.createWorkOrder(job.name, techName, techName);
                      setWoId(result.wo_id);
                      toast({ title: `Work Order ${result.wo_id} created` });
                      navigate(`/tech/workorder/${result.wo_id}`);
                    } catch (err) {
                      toast({ title: 'WO creation failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
                    } finally {
                      setCreatingWO(false);
                    }
                  }}>
                  <Briefcase className="w-4 h-4 mr-1" />
                  {creatingWO ? 'Creating…' : 'Create WO'}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/tech/job/${jobId}/add-items`)}
                className="text-green-600 border-green-600 hover:bg-green-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Items
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {job.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.item_name}</p>
                    <p className="text-sm text-gray-500">
                      {item.zone ? <span className="font-semibold text-blue-700">Zone: {item.zone} · </span> : ''}
                      {item.item_code}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600">Qty: {item.qty}</p>
                    <p className="text-gray-800 font-medium">${item.amount?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
            {job.grand_total != null && (
              <p className="text-sm font-semibold text-gray-800 mt-3 text-right">
                Total: ${job.grand_total?.toFixed(2)}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Quotation: <span className="font-medium">{job.name}</span>
            </p>
            <p className="text-xs text-amber-600 mt-2">
              Note: Additional items require supplemental estimate
            </p>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-white">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Tech Notes</CardTitle>
            {!editingNotes && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingNotes(true)}
                className="text-green-600"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {editingNotes ? (
              <div className="space-y-3">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this job..."
                  rows={4}
                  className="text-base"
                />
                <div className="flex space-x-2">
                  <Button
                    onClick={handleSaveNotes}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Notes
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingNotes(false);
                      setNotes(job.notes || '');
                    }}
                    variant="outline"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">{notes || 'No notes yet'}</p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          {/* Send the estimate for customer approval — available any time before the
              job is completed/cancelled, since a tech may need to re-send it or send
              it after adding items on site. Hidden once the customer already approved
              (custom_approval_status "Approved"), where the estimate is locked. */}
          {job.status !== 'Completed' && job.status !== 'Cancelled'
            && job.custom_approval_status !== 'Approved' && (
            <Button
              onClick={handleSendEstimate}
              disabled={sendingEstimate}
              variant="outline"
              className="w-full h-12 border-2 border-amber-600 text-amber-700 hover:bg-amber-50 text-base font-semibold"
            >
              <Mail className="w-5 h-5 mr-2" />
              {sendingEstimate
                ? 'Sending…'
                : job.custom_approval_status === 'Sent for Approval'
                  ? 'Re-send Estimate for Approval'
                  : 'Send Estimate for Approval'}
            </Button>
          )}

          {job.status === 'Scheduled' && (
            <>
              <Button
                onClick={handleMarkInRoute}
                disabled={inRoutePending || gettingLocation}
                className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold"
              >
                <Car className="w-5 h-5 mr-2" />
                {inRoutePending ? 'Sending…' : gettingLocation ? 'Getting location…' : 'Mark In Route (Notify Customer)'}
              </Button>
              <Button
                onClick={handleStartJob}
                disabled={isStarting}
                variant="outline"
                className="w-full h-12 border-2 border-green-600 text-green-600 hover:bg-green-50"
              >
                {isStarting ? 'Starting...' : 'Start Job (On Site)'}
              </Button>
            </>
          )}

          {job.status === 'En Route' && (
            <Button
              onClick={handleStartJob}
              disabled={isStarting}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold"
            >
              {isStarting ? 'Starting...' : 'Start Job (Arrived On Site)'}
            </Button>
          )}

          {job.status === 'In Progress' && (
            <>
              <Button
                onClick={handleCompleteJob}
                disabled={completingJob}
                className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                {completingJob ? 'Completing…' : 'Complete Job'}
              </Button>
              <Button
                onClick={() => navigate(`/tech/job/${jobId}/approval`)}
                variant="outline"
                className="w-full h-12 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-base font-semibold"
              >
                Request Customer Approval
              </Button>
            </>
          )}

          {job.status === 'Completed' && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-green-900">Job Completed</p>
              </div>

              {invoiceName ? (
                <div className="mt-3 border-t border-green-200 pt-3 text-center">
                  <p className="text-sm text-green-700">Invoice <strong>{invoiceName}</strong> created</p>
                  {invoiceSent ? (
                    <p className="text-sm text-green-600 mt-2 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Sent to customer for payment
                    </p>
                  ) : (
                    <Button
                      onClick={handleSendInvoice}
                      disabled={sendingInvoice}
                      className="w-full h-12 mt-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    >
                      {sendingInvoice ? 'Sending…' : 'Send Invoice to Customer'}
                    </Button>
                  )}
                  {/* Take payment on site — cash/check/Zelle/card handed to the tech.
                      Separate, deliberate press; sending the invoice never records one. */}
                  {paymentRecorded ? (
                    <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      ${paymentRecorded.amount.toFixed(2)} {paymentRecorded.mode} recorded
                      {paymentRecorded.entry ? ` (${paymentRecorded.entry})` : ''}
                    </p>
                  ) : (
                    <>
                      {/* Charges the customer's card right here, right now.
                          Manual-only — nothing charges without this press. */}
                      <Button
                        onClick={() => setChargeOpen(true)}
                        className="w-full h-12 mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold"
                      >
                        Process Payment (Charge Card)
                      </Button>
                      <Button
                        onClick={() => setPayDialog({
                          open: true,
                          amount: job.grand_total != null ? String(job.grand_total.toFixed(2)) : '',
                          mode: 'Cash',
                          reference_no: '',
                        })}
                        variant="outline"
                        className="w-full h-12 mt-3 border-2 border-green-700 text-green-700 hover:bg-green-50 font-semibold"
                      >
                        Record Payment Taken On Site
                      </Button>
                    </>
                  )}
                  {woId && (
                    <button
                      onClick={() => navigate(`/tech/workorder/${woId}`)}
                      className="text-xs text-green-700 underline mt-3 block mx-auto"
                    >
                      Extra work needed? Add a supplemental estimate
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-green-700 mt-1 text-center">Invoice could not be created — check the Work Order.</p>
              )}
            </div>
          )}

          {job.status === 'Cancelled' && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
              <p className="font-semibold text-red-900">Job Cancelled</p>
            </div>
          )}
        </div>
      </div>

      {/* Powered By Footer */}
      <div className="mt-8 py-6 border-t border-gray-200 text-center text-xs text-gray-500 bg-white">
        Powered by <span className="font-bold text-gray-900">Boatman Systems™</span>
      </div>

      {/* ETA Dialog */}
      {etaDialog.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-lg text-gray-900">Confirm ETA</h3>
            {etaDialog.durationText && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center">
                <p className="text-sm text-green-700 font-medium">Google Maps: {etaDialog.durationText} away</p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Minutes until arrival</label>
              <Input
                type="number"
                min="1"
                max="240"
                value={etaDialog.minutes}
                onChange={e => setEtaDialog(d => ({ ...d, minutes: e.target.value }))}
                className="h-14 text-2xl text-center font-semibold"
                autoFocus
              />
            </div>
            <p className="text-sm text-gray-500 text-center">
              ETA: <span className="font-medium text-gray-800">{_etaStr(etaDialog.minutes)}</span>
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-12"
                onClick={() => setEtaDialog(d => ({ ...d, open: false }))}>
                Cancel
              </Button>
              <Button className="flex-1 h-12 bg-green-600 hover:bg-green-700 font-semibold"
                onClick={confirmEta}>
                Send SMS
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment dialog (on-site cash/check/Zelle/card) */}
      {payDialog.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
            <h3 className="font-bold text-lg text-gray-900">Record Payment</h3>
            <p className="text-xs text-gray-500">Invoice {invoiceName}</p>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Amount ($)</label>
              <Input
                type="number" min="0" step="0.01" inputMode="decimal"
                value={payDialog.amount}
                onChange={e => setPayDialog(d => ({ ...d, amount: e.target.value }))}
                className="h-14 text-2xl text-center font-semibold"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Payment Method</label>
              <select
                className="w-full border rounded-md px-3 py-3 text-base"
                value={payDialog.mode}
                onChange={e => setPayDialog(d => ({ ...d, mode: e.target.value }))}
              >
                <option>Cash</option>
                <option>Check</option>
                <option>Credit Card</option>
                <option>Zelle</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Reference / Check No.
                {payDialog.mode !== 'Cash' && payDialog.mode !== 'Credit Card' && ' (required)'}
              </label>
              <Input
                placeholder={payDialog.mode === 'Check' ? 'Check number' : 'Confirmation / reference'}
                value={payDialog.reference_no}
                onChange={e => setPayDialog(d => ({ ...d, reference_no: e.target.value }))}
                className="h-12"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-12"
                onClick={() => setPayDialog(d => ({ ...d, open: false }))}>
                Cancel
              </Button>
              <Button className="flex-1 h-12 bg-green-600 hover:bg-green-700 font-semibold"
                disabled={savingPayment}
                onClick={handleSavePayment}>
                {savingPayment ? 'Saving…' : 'Record Payment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Process Payment — live Authorize.net card charge (Accept.js tokenized) */}
      {chargeOpen && invoiceName && (
        <ProcessPaymentDialog
          invoice={{ name: invoiceName, outstanding_amount: job?.grand_total }}
          surface="Tech App"
          onClose={() => setChargeOpen(false)}
          onSuccess={(data) => {
            setPaymentRecorded({
              amount: parseFloat(data?.amount ?? job?.grand_total ?? 0) || 0,
              mode: 'Credit Card',
              entry: data?.trans_id || '',
            });
            setChargeOpen(false);
          }}
        />
      )}

      {/* Chat with Office dialog */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 space-y-3">
            <h3 className="font-bold text-gray-900">Message Office</h3>
            {job && <p className="text-xs text-gray-500">Job: {job.name}</p>}
            <textarea
              className="w-full border rounded-md p-2 text-sm resize-none"
              rows={4}
              placeholder="e.g. Need to add an item, adjust price, customer question…"
              value={chatText}
              onChange={e => setChatText(e.target.value)}
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setChatOpen(false)}>Cancel</Button>
              <Button
                disabled={chatSending || !chatText.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={async () => {
                  setChatSending(true);
                  try {
                    const techName = localStorage.getItem('technicianName') || 'Tech';
                    await crmBackend.sendChatMessage(chatText.trim(), techName, job?.name || '');
                    toast({ title: 'Message sent to office' });
                    setChatOpen(false);
                  } catch (err) {
                    toast({ title: 'Send failed', description: err.message, variant: 'destructive' });
                  } finally {
                    setChatSending(false);
                  }
                }}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                {chatSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <AppVersion />
    </div>
  );
};

export default TechJobDetails;
